import { useScrollHashIntoView } from 'developer-docs-shared-components/hooks/useScrollHashIntoView';
import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { mediaQueries, MOBILE_MENU_BREAKPOINT } from 'developer-docs-shared-components/utils/mediaQueries';
import { getUsageTracker } from '../utils/usage-tracker/index';
import useLocale from 'developer-docs-shared-components/hooks/useLocale';
import LayoutPublic, { SCROLL_CONTAINER_ID, StickyWrapper } from '../components/LayoutPublic';
import Article from '../components/Article';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import DocsBreadcrumbs from '../components/DocsBreadcrumbs';
import SecondaryNav from '../components/SecondaryNav';
import { useMetadata } from 'developer-docs-shared-components/hooks/useMetadata';
import { useGetBreadcrumbsByUrl } from '../hooks/useGetBreadcrumbsByUrl';
import Toc from '../components/Toc';
import { MAIN_CONTENT_ID } from '../config/accessibility';
import I18n from 'I18n';
import { MDXLayoutContextProvider } from 'developer-docs-shared-components/context/MDXLayoutContext';
import UIButton from 'UIComponents/button/UIButton';
import UIDialog from 'UIComponents/dialog/UIDialog';
import UIForm from 'UIComponents/form/UIForm';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILink from 'UIComponents/link/UILink';
import UITextInput from 'UIComponents/input/UITextInput';
import H5 from 'UIComponents/elements/headings/H5';
import useMDXContent from '../hooks/useMDXContent';
import SkeletonContainer from 'conversations-skeleton-state/components/SkeletonContainer';
import SkeletonBase from 'conversations-skeleton-state/components/SkeletonBase';
import { NavMarker } from 'react-rhumb';
import UIErrorMessage from 'UIComponents/error/UIErrorMessage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Centers the article contents and applies a max-width. Descendants of this
 * layout can have negative margins which expand into the gutter of the article
 * at desktop via `GutterContainer`.
 * TODO: Can we do this to article directly?
 */
const GutterWrapper = styled.div.withConfig({
  displayName: "MDXPage__GutterWrapper",
  componentId: "jxrjj7-0"
})(["", "{margin:0 auto;max-width:680px;padding:0 16px 40px 16px;}"], mediaQueries.aboveWidth(MOBILE_MENU_BREAKPOINT));
const PasswordProtectionDialog = styled(UIDialog).withConfig({
  displayName: "MDXPage__PasswordProtectionDialog",
  componentId: "jxrjj7-1"
})(["max-width:800px;width:100%;"]);
const PasswordInnerContent = styled.div.withConfig({
  displayName: "MDXPage__PasswordInnerContent",
  componentId: "jxrjj7-2"
})(["max-width:650px;padding:40px 64px;"]);
function MDXPage({
  ctxKey,
  secondaryNavConfig
}) {
  var _mdx$metaDescription;
  const locale = useLocale();
  const [password, setPassword] = useState('');
  const [validationMessage, setValidationMessage] = useState(null);
  const [passwordIsCorrect, setPasswordIsCorrect] = useState(false);
  const {
    mdx,
    loading,
    mdxError
  } = useMDXContent({
    ctxKey,
    locale
  });
  const hasNotClearedPassword = (mdx === null || mdx === void 0 ? void 0 : mdx.password) !== undefined && !passwordIsCorrect;
  const hasClearedPassword = (mdx === null || mdx === void 0 ? void 0 : mdx.password) === undefined || !hasNotClearedPassword;

  /**
   * Set metadata for SEO
   */
  const titleParams = ['developer-docs-ui.htmlTitles.mdxDoc', {
    pageTopic: (mdx === null || mdx === void 0 ? void 0 : mdx.title) || ''
  }];
  useMetadata({
    title: hasClearedPassword ? I18n.text(...titleParams) : I18n.text('developer-docs-ui.passwordProtect.passwordProtected'),
    description: hasClearedPassword ? (_mdx$metaDescription = mdx === null || mdx === void 0 ? void 0 : mdx.metaDescription) !== null && _mdx$metaDescription !== void 0 ? _mdx$metaDescription : '' : I18n.text('developer-docs-ui.passwordProtect.passwordProtected'),
    featuredImage: mdx === null || mdx === void 0 ? void 0 : mdx.featuredImage,
    featuredImageAltText: mdx === null || mdx === void 0 ? void 0 : mdx.featuredImageAltText,
    noIndex: mdx === null || mdx === void 0 ? void 0 : mdx.noIndex,
    // If mdx is null, then it is not loaded yet
    isContentLoaded: !!mdx
  });

  // If there are named exports as well, *.default needs to be used
  const Doc = (mdx === null || mdx === void 0 ? void 0 : mdx.default) || mdx;
  useEffect(() => {
    getUsageTracker().track('pageView', {
      screen: 'mdxDoc',
      subscreen: ctxKey
    });
  }, [ctxKey]);
  const languageForDevCommunity = locale === 'pt-br' ? locale : locale.split('-')[0];
  const breadcrumbs = useGetBreadcrumbsByUrl({
    title: (mdx === null || mdx === void 0 ? void 0 : mdx.title) || ''
  });
  const onSubmitPassword = useCallback(event => {
    event.preventDefault();
    if (password === (mdx === null || mdx === void 0 ? void 0 : mdx.password)) {
      setPasswordIsCorrect(true);
    } else {
      setValidationMessage(I18n.text('developer-docs-ui.passwordProtect.validationError'));
    }
  }, [mdx === null || mdx === void 0 ? void 0 : mdx.password, password, setPasswordIsCorrect]);
  useScrollHashIntoView(MAIN_CONTENT_ID, !loading);
  return /*#__PURE__*/_jsx(MDXLayoutContextProvider, {
    value: true,
    children: /*#__PURE__*/_jsxs(LayoutPublic, {
      hasClearedPassword: hasClearedPassword,
      maxContentWidth: 1293,
      mdxDocPageId: mdx === null || mdx === void 0 ? void 0 : mdx.pageId,
      sideNavComponent: /*#__PURE__*/_jsx(SecondaryNav, {
        navConfig: secondaryNavConfig
      }),
      children: [hasNotClearedPassword && /*#__PURE__*/_jsx(UIFlex, {
        justify: "center",
        className: "m-top-9",
        children: /*#__PURE__*/_jsx(PasswordProtectionDialog, {
          children: /*#__PURE__*/_jsx(UIFlex, {
            className: "w-100",
            justify: "center",
            children: /*#__PURE__*/_jsxs(PasswordInnerContent, {
              children: [/*#__PURE__*/_jsx(H5, {
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "developer-docs-ui.passwordProtect.title"
                })
              }), /*#__PURE__*/_jsx(FormattedMessage, {
                message: "developer-docs-ui.passwordProtect.prompt"
              }), /*#__PURE__*/_jsxs(UIForm, {
                className: "m-top-4",
                onSubmit: onSubmitPassword,
                children: [/*#__PURE__*/_jsx(UIFormControl, {
                  "aria-label": I18n.text('developer-docs-ui.passwordProtect.label'),
                  error: validationMessage !== null,
                  validationMessage: validationMessage,
                  children: /*#__PURE__*/_jsx(UITextInput, {
                    name: "password",
                    type: "password",
                    autoComplete: "current-password",
                    required: true,
                    placeholder: I18n.text('developer-docs-ui.passwordProtect.placeholder'),
                    onChange: e => {
                      setValidationMessage(null);
                      setPassword(e.currentTarget.value);
                    }
                  })
                }), /*#__PURE__*/_jsx(UIButton, {
                  className: "m-y-4",
                  type: "submit",
                  use: "primary",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "developer-docs-ui.passwordProtect.submit"
                  })
                })]
              }), /*#__PURE__*/_jsx(FormattedJSXMessage, {
                message: "developer-docs-ui.passwordProtect.findOutMore_jsx",
                elements: {
                  Link: UILink
                },
                options: {
                  href: `https://community.hubspot.com/t5/Product-Betas/ct-p/product-betas${languageForDevCommunity !== 'en-us' ? `?profile.language=${languageForDevCommunity}` : ''}`,
                  external: true
                }
              })]
            })
          })
        })
      }), hasClearedPassword && /*#__PURE__*/_jsxs(_Fragment, {
        children: [(mdx === null || mdx === void 0 ? void 0 : mdx.toc) && (mdx === null || mdx === void 0 ? void 0 : mdx.toc.length) > 0 && /*#__PURE__*/_jsx(StickyWrapper, {
          children: /*#__PURE__*/_jsx(Toc, {
            containerId: SCROLL_CONTAINER_ID,
            toc: mdx === null || mdx === void 0 ? void 0 : mdx.toc,
            resources: mdx === null || mdx === void 0 ? void 0 : mdx.resources
          })
        }), /*#__PURE__*/_jsx(Article, {
          "data-test-id": "docs-content",
          children: /*#__PURE__*/_jsxs(GutterWrapper, {
            children: [/*#__PURE__*/_jsx(DocsBreadcrumbs, {
              breadcrumbs: breadcrumbs
            }), loading ? /*#__PURE__*/_jsx(SkeletonContainer, {
              height: "auto",
              width: "auto",
              padding: 0,
              children: /*#__PURE__*/_jsxs(UIFlex, {
                direction: "column",
                gap: 24,
                children: [/*#__PURE__*/_jsx(SkeletonBase, {
                  padding: 24
                }), /*#__PURE__*/_jsx(SkeletonBase, {
                  padding: 64
                }), /*#__PURE__*/_jsx(SkeletonBase, {
                  padding: 128
                })]
              })
            }) : mdxError ? /*#__PURE__*/_jsx(NavMarker, {
              name: "MDX_DOC_PAGE_ERROR",
              children: /*#__PURE__*/_jsx(UIFlex, {
                align: "center",
                justify: "center",
                children: /*#__PURE__*/_jsx(UIErrorMessage, {
                  type: "badRequest",
                  "data-test-id": "mdx-error"
                })
              })
            }) : /*#__PURE__*/_jsx(NavMarker, {
              name: "MDX_DOC_PAGE_LOAD",
              children: /*#__PURE__*/_jsx("div", {
                "data-test-id": "docs-mdx",
                children: /*#__PURE__*/_jsx(Doc, {})
              })
            })]
          })
        })]
      })]
    })
  });
}
export default MDXPage;